import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AideFinanciere.css';
import { 
  FaMoneyBillWave,
  FaEuroSign,
  FaUniversity,
  FaHandHoldingUsd,
  FaGraduationCap,
  FaBuilding,
  FaRegClock,
  FaArrowLeft,
  FaCheckCircle,
  FaCalendarAlt,
  FaFileAlt,
  FaChartLine,
  FaHeart,
  FaUsers,
  FaGlobe,
  FaBriefcase,
  FaHome,
  FaBook,
  FaTrophy,
  FaSearch,
  FaRegEnvelope
} from 'react-icons/fa';

const AideFinanciere = () => {
  const navigate = useNavigate();

  const scholarships = [
    {
      name: "Bourse sur critères sociaux (CROUS)",
      amount: "Jusqu'à 6 334€/an",
      eligibility: "Étudiants européens résidant en France, critères sociaux",
      deadline: "Mai - Juin",
      documents: ["Dossier social étudiant (DSE)", "Revenus des parents", "Justificatif de domicile"],
      icon: <FaUniversity />,
      color: "#2e8b57"
    },
    {
      name: "Bourse Eiffel",
      amount: "1 181€/mois",
      eligibility: "Étudiants internationaux, excellents dossiers académiques",
      deadline: "Janvier",
      documents: ["CV", "Lettres de recommandation", "Projet d'études", "Relevés de notes"],
      icon: <FaTrophy />,
      color: "#ffd700"
    },
    {
      name: "Bourse Erasmus+",
      amount: "200-500€/mois",
      eligibility: "Étudiants en mobilité internationale",
      deadline: "Selon université",
      documents: ["Convention de mobilité", "Projet pédagogique", "Carte étudiant"],
      icon: <FaGlobe />,
      color: "#1e90ff"
    },
    {
      name: "Aide au mérite",
      amount: "900€/an (cumulable avec bourse)",
      eligibility: "Boursiers ayant obtenu mention Très Bien au bac",
      deadline: "Septembre",
      documents: ["Relevé de notes", "Certificat de scolarité", "Dossier CROUS"],
      icon: <FaGraduationCap />,
      color: "#ff8c00"
    },
    {
      name: "Bourse de recherche doctorale",
      amount: "1 400-2 200€/mois",
      eligibility: "Doctorants, projets de recherche",
      deadline: "Variable",
      documents: ["Projet de thèse", "Directeur de thèse", "Publications"],
      icon: <FaBook />,
      color: "#6c5ce7"
    }
  ];

  const financialAids = [
    {
      title: "APL - Aide Personnalisée au Logement",
      amount: "Jusqu'à 250€/mois",
      description: "Aide au paiement du loyer",
      conditions: "Logement conventionné, ressources modestes",
      steps: ["Créer compte CAF", "Remplir formulaire", "Fournir justificatifs"],
      icon: <FaHome />,
      color: "#32cd32"
    },
    {
      title: "Prime d'activité",
      amount: "Jusqu'à 200€/mois",
      description: "Complément de revenus pour étudiants actifs",
      conditions: "Âge > 18 ans, activité professionnelle, ressources modestes",
      steps: ["Simulation en ligne", "Dépôt dossier CAF", "Déclaration trimestrielle"],
      icon: <FaBriefcase />,
      color: "#ff6b6b"
    },
    {
      title: "Fonds d'Aide aux Jeunes (FAJ)",
      amount: "Jusqu'à 1 000€",
      description: "Aide ponctuelle en situation difficile",
      conditions: "18-25 ans, difficultés financières exceptionnelles",
      steps: ["Contacter Mission Locale", "Entretien social", "Dossier à constituer"],
      icon: <FaHandHoldingUsd />,
      color: "#ffa500"
    },
    {
      title: "Aide annuelle du CROUS",
      amount: "Jusqu'à 1 000€",
      description: "Aide ponctuelle pour situations exceptionnelles",
      conditions: "Étudiant boursier ou non, difficultés imprévues",
      steps: ["Dossier à télécharger", "Pièces justificatives", "Commission sociale"],
      icon: <FaHeart />,
      color: "#ff69b4"
    }
  ];

  const studentJobs = [
    {
      type: "Travail à temps partiel",
      hours: "Jusqu'à 20h/semaine",
      earnings: "500-800€/mois",
      tips: "Serveur, baby-sitting, cours particuliers"
    },
    {
      type: "Contrat d'apprentissage",
      hours: "Alternance 1-3 jours/semaine",
      earnings: "800-1 500€/mois",
      tips: "Formation professionnelle, expérience valorisée"
    },
    {
      type: "Stages rémunérés",
      hours: "35h/semaine",
      earnings: "554-1 500€/mois",
      tips: "Minimum légal 4,05€/h, souvent mieux payés"
    },
    {
      type: "Vacations universitaires",
      hours: "Flexible",
      earnings: "11,65-20€/h",
      tips: "Travaux pratiques, surveillance examens"
    }
  ];

  const emergencyFunds = [
    {
      name: "Fonds d'urgence CROUS",
      description: "Aide financière exceptionnelle pour situations critiques",
      delay: "15 jours",
      maxAmount: "1 000€",
      contact: "Service social CROUS"
    },
    {
      name: "Secours d'urgence CAF",
      description: "Avance sur prestations en cas de retard",
      delay: "48-72h",
      maxAmount: "Variable",
      contact: "Espace personnel CAF"
    },
    {
      name: "Aide sociale départementale",
      description: "Pour étudiants sans ressources",
      delay: "1 mois",
      maxAmount: "Variable",
      contact: "Département de résidence"
    },
    {
      name: "Assistants sociaux universitaires",
      description: "Accompagnement personnalisé",
      delay: "Rendez-vous sous 8 jours",
      maxAmount: "Accompagnement",
      contact: "Service social universitaire"
    }
  ];

  const tips = [
    {
      title: "Anticiper les démarches",
      description: "Les aides ont des délais de traitement. Déposez vos dossiers 2-3 mois avant le début des cours.",
      icon: <FaRegClock />
    },
    {
      title: "Simuler ses droits",
      description: "Utilisez les simulateurs CAF, CROUS pour estimer vos aides potentielles.",
      icon: <FaChartLine />
    },
    {
      title: "Constitution du dossier",
      description: "Préparez tous vos justificatifs : revenus, logement, identité, scolarité.",
      icon: <FaFileAlt />
    },
    {
      title: "Suivi et relances",
      description: "N'hésitez pas à contacter les services pour suivre vos dossiers.",
      icon: <FaSearch />
    },
    {
      title: "Cumul des aides",
      description: "Certaines aides sont cumulables (APL + bourse + prime activité).",
      icon: <FaHandHoldingUsd />
    },
    {
      title: "Étudiants internationaux",
      description: "Renseignez-vous sur les bourses spécifiques à votre pays d'origine.",
      icon: <FaGlobe />
    }
  ];

  const budgetPlanner = [
    {
      category: "Loyer",
      average: "400-700€",
      tips: "Colocation, APL, logement CROUS"
    },
    {
      category: "Alimentation",
      average: "200-300€",
      tips: "Restos du Cœur, marchés, cuisine maison"
    },
    {
      category: "Transport",
      average: "30-75€",
      tips: "Navigo Imagine R, vélo, covoiturage"
    },
    {
      category: "Assurance",
      average: "15-50€",
      tips: "Mutuelle étudiante, assurances logement"
    },
    {
      category: "Loisirs",
      average: "50-100€",
      tips: "Tarifs étudiants, carte culture"
    },
    {
      category: "Divers",
      average: "100-150€",
      tips: "Vêtements, téléphone, fournitures"
    }
  ];

  const usefulLinks = [
    {
      name: "CROUS",
      url: "https://www.crous.fr",
      description: "Bourses et aides sociales"
    },
    {
      name: "CAF",
      url: "https://www.caf.fr",
      description: "APL, prime d'activité"
    },
    {
      name: "Campus France",
      url: "https://www.campusfrance.org",
      description: "Bourses internationales"
    },
    {
      name: "Service Public",
      url: "https://www.service-public.fr",
      description: "Toutes les aides"
    },
    {
      name: "BPI France",
      url: "https://www.bpifrance.fr",
      description: "Aides à l'entrepreneuriat"
    }
  ];

  return (
    <div className="af-page">
      {/* Hero Section */}
      <div className="af-hero af-hero--finance">
        <div className="af-hero__background"></div>
        <div className="af-hero__content">
          <button className="af-back-button" onClick={() => navigate('/association')}>
            <FaArrowLeft /> Retour aux services
          </button>
          <div className="af-hero__icon">
            <FaMoneyBillWave />
          </div>
          <h1 className="af-hero__title">Aide Financière</h1>
          <p className="af-hero__subtitle">Information et assistance pour les aides financières et bourses</p>
        </div>
      </div>

      <div className="af-content">
        <div className="af-container">
          {/* Introduction */}
          <div className="af-intro">
            <h2 className="af-section-title">Financez vos études en France</h2>
            <p className="af-intro__text">
              De nombreuses aides financières existent pour soutenir les étudiants étrangers en France.
              Bourses, aides au logement, dispositifs d'urgence... Notre service vous guide pour identifier
              toutes les aides auxquelles vous pouvez prétendre et vous accompagne dans vos démarches.
            </p>
            <div className="af-stats">
              <div className="af-stat">
                <div className="af-stat__number">+50</div>
                <div className="af-stat__label">Dispositifs d'aide</div>
              </div>
              <div className="af-stat">
                <div className="af-stat__number">6 334€</div>
                <div className="af-stat__label">Bourse max/an</div>
              </div>
              <div className="af-stat">
                <div className="af-stat__number">250€</div>
                <div className="af-stat__label">APL moyenne/mois</div>
              </div>
            </div>
          </div>

          {/* Bourses */}
          <div className="af-section">
            <h2 className="af-section-title">Bourses d'études</h2>
            <div className="af-grid af-grid--scholarships">
              {scholarships.map((scholarship, index) => (
                <div className="af-card af-card--scholarship" key={index} style={{ borderTopColor: scholarship.color }}>
                  <div className="af-card__icon" style={{ color: scholarship.color }}>
                    {scholarship.icon}
                  </div>
                  <h3 className="af-card__title">{scholarship.name}</h3>
                  <div className="af-card__amount">{scholarship.amount}</div>
                  <div className="af-card__detail">
                    <FaCheckCircle className="af-card__detail-icon" />
                    <span>{scholarship.eligibility}</span>
                  </div>
                  <div className="af-card__detail">
                    <FaCalendarAlt className="af-card__detail-icon" />
                    <span>Date limite : {scholarship.deadline}</span>
                  </div>
                  <div className="af-card__documents">
                    <strong>Documents :</strong>
                    <ul>
                      {scholarship.documents.map((doc, i) => (
                        <li key={i}>{doc}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Aides sociales */}
          <div className="af-section">
            <h2 className="af-section-title">Aides sociales</h2>
            <div className="af-grid af-grid--aids">
              {financialAids.map((aid, index) => (
                <div className="af-card af-card--aid" key={index}>
                  <div className="af-card__icon" style={{ color: aid.color }}>
                    {aid.icon}
                  </div>
                  <h3 className="af-card__title">{aid.title}</h3>
                  <div className="af-card__amount">{aid.amount}</div>
                  <p className="af-card__description">{aid.description}</p>
                  <div className="af-card__conditions">
                    <strong>Conditions :</strong> {aid.conditions}
                  </div>
                  <div className="af-card__steps">
                    <strong>Démarches :</strong>
                    <ul>
                      {aid.steps.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Jobs étudiants */}
          <div className="af-section">
            <h2 className="af-section-title">Compléter ses revenus</h2>
            <div className="af-grid af-grid--jobs">
              {studentJobs.map((job, index) => (
                <div className="af-card af-card--job" key={index}>
                  <div className="af-card__job-type">{job.type}</div>
                  <div className="af-card__job-info">
                    <FaRegClock className="af-card__job-icon" />
                    <span>{job.hours}</span>
                  </div>
                  <div className="af-card__job-info">
                    <FaEuroSign className="af-card__job-icon" />
                    <span>{job.earnings}</span>
                  </div>
                  <div className="af-card__job-tips">
                    <FaCheckCircle className="af-card__tip-icon" />
                    <span>{job.tips}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Aides d'urgence */}
          <div className="af-section">
            <h2 className="af-section-title">Aides d'urgence</h2>
            <div className="af-grid af-grid--emergency">
              {emergencyFunds.map((fund, index) => (
                <div className="af-card af-card--emergency" key={index}>
                  <h3 className="af-card__title">{fund.name}</h3>
                  <p className="af-card__description">{fund.description}</p>
                  <div className="af-card__emergency-details">
                    <div className="af-card__detail">
                      <FaRegClock className="af-card__detail-icon" />
                      <span>Délai : {fund.delay}</span>
                    </div>
                    <div className="af-card__detail">
                      <FaEuroSign className="af-card__detail-icon" />
                      <span>Montant max : {fund.maxAmount}</span>
                    </div>
                    <div className="af-card__detail">
                      <FaRegEnvelope className="af-card__detail-icon" />
                      <span>Contact : {fund.contact}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Budget prévisionnel */}
          <div className="af-section">
            <h2 className="af-section-title">Budget étudiant prévisionnel</h2>
            <div className="af-budget-table">
              {budgetPlanner.map((item, index) => (
                <div className="af-budget-row" key={index}>
                  <div className="af-budget-row__category">{item.category}</div>
                  <div className="af-budget-row__average">{item.average}</div>
                  <div className="af-budget-row__tips">{item.tips}</div>
                </div>
              ))}
              <div className="af-budget-total">
                <span>Total mensuel estimé</span>
                <span className="af-budget-total__amount">800 - 1 500€</span>
              </div>
            </div>
          </div>

          {/* Conseils pratiques */}
          <div className="af-section">
            <h2 className="af-section-title">Conseils pour optimiser vos aides</h2>
            <div className="af-grid af-grid--tips">
              {tips.map((tip, index) => (
                <div className="af-card af-card--tip" key={index}>
                  <div className="af-card__tip-icon">{tip.icon}</div>
                  <h3 className="af-card__title">{tip.title}</h3>
                  <p className="af-card__description">{tip.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Liens utiles */}
          <div className="af-section">
            <h2 className="af-section-title">Liens utiles</h2>
            <div className="af-grid af-grid--links">
              {usefulLinks.map((link, index) => (
                <a 
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="af-link-card"
                  key={index}
                >
                  <div className="af-link-card__name">{link.name}</div>
                  <div className="af-link-card__desc">{link.description}</div>
                </a>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="af-contact">
            <h2 className="af-section-title af-section-title--centered">Besoin d'aide pour vos démarches ?</h2>
            <p className="af-contact__text">Nous vous accompagnons dans la constitution de vos dossiers d'aide financière</p>
            <div className="af-contact-buttons">
              <Link to="/contact" className="af-button af-button--primary">
                <FaHandHoldingUsd /> Simuler mes droits
              </Link>
              <Link to="/rendez-vous" className="af-button af-button--secondary">
                <FaFileAlt /> Aide dossier bourse
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AideFinanciere;